
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Slab } from "@/types/slab";
import { fetchSlabsFromSupabase } from "@/lib/supabase/slabs";
import { SlabDetails } from "@/components/SlabDetails";
import { SearchBar } from "@/components/catalog/SearchBar";
import { FilterBar } from "@/components/catalog/FilterBar";
import { SlabResults } from "@/components/catalog/SlabResults";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";

function Index() {
  const [selectedSlab, setSelectedSlab] = React.useState<Slab | null>(null);
  const [search, setSearch] = React.useState("");
  const [location, setLocation] = React.useState("all-locations");
  const [type, setType] = React.useState("all-types");
  const [sortBy, setSortBy] = React.useState("name-asc");
  const [viewMode, setViewMode] = React.useState<'grid' | 'list'>('grid');
  const isMobile = useIsMobile();

  // Busca os slabs
  const {
    data: slabs = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['slabs'],
    queryFn: fetchSlabsFromSupabase,
    meta: {
      onError: () => {
        toast.error('Falha ao carregar os slabs');
      }
    }
  });

  // Busca os tipos de material
  const {
    data: materialTypes = [],
    isLoading: isLoadingTypes
  } = useQuery({
    queryKey: ['materialTypes'],
    queryFn: async () => {
      console.log('Buscando tipos de material...');
      const { data, error } = await supabase
        .from('cat_material_prefixes')
        .select('material_type')
        .order('material_type');

      if (error) {
        console.error('Erro ao carregar tipos:', error);
        throw error;
      }

      // Extrai apenas os valores de material_type e remove duplicatas
      const uniqueTypes = Array.from(new Set(data.map(d => d.material_type)));
      console.log('Tipos únicos encontrados:', uniqueTypes);
      return uniqueTypes;
    },
    meta: {
      onError: () => {
        toast.error('Falha ao carregar os tipos de material');
      }
    }
  });

  const locations = React.useMemo(() => {
    const uniqueLocations = new Set(slabs.map(slab => slab.location).filter(Boolean));
    return ["all-locations", ...Array.from(uniqueLocations)];
  }, [slabs]);

  const types = React.useMemo(() => {
    if (isLoadingTypes) return ["all-types"];
    console.log('Types para FilterBar:', ["all-types", ...materialTypes]);
    return ["all-types", ...materialTypes];
  }, [materialTypes, isLoadingTypes]);

  const filteredSlabs = React.useMemo(() => {
    return slabs.filter(slab => {
      const searchLower = search.toLowerCase();
      const matchesSearch = !search || 
        slab.code?.toLowerCase().includes(searchLower) || 
        slab.name?.toLowerCase().includes(searchLower);
      const matchesLocation = location === "all-locations" || 
        slab.location?.toLowerCase() === location.toLowerCase();
      const matchesType = type === "all-types" || 
        slab.type?.toLowerCase() === type.toLowerCase();
      return matchesSearch && matchesLocation && matchesType;
    }).sort((a, b) => {
      switch (sortBy) {
        case "name-asc":
          return (a.name || '').localeCompare(b.name || '');
        case "name-desc":
          return (b.name || '').localeCompare(a.name || '');
        case "price-asc":
          return (a.price || 0) - (b.price || 0);
        case "price-desc":
          return (b.price || 0) - (a.price || 0);
        case "quantity-asc":
          return (a.quantity || 0) - (b.quantity || 0);
        case "quantity-desc":
          return (b.quantity || 0) - (a.quantity || 0);
        default:
          return (a.name || '').localeCompare(a.name || '');
      }
    });
  }, [slabs, search, location, type, sortBy]);

  const handleSlabUpdate = async () => {
    await refetch();
  };

  console.log('Selected Slab:', selectedSlab); // Debug log

  return (
    <div className="container py-2">
      <div className="sticky top-0 bg-background z-40 pb-2 border-b">
        <div className={`flex flex-col gap-2 ${!isMobile ? 'md:flex-row md:items-center md:justify-between' : ''}`}>
          <div className="flex items-center gap-2">
            <div className="brand-name">
              <span className="brand-max">Max</span>
              <span className="brand-catalog">Catalog</span>
            </div>
            <SearchBar value={search} onChange={setSearch} />
          </div>
          
          <FilterBar 
            location={location} 
            type={type} 
            sortBy={sortBy} 
            viewMode={viewMode} 
            locations={locations} 
            types={types} 
            onLocationChange={setLocation} 
            onTypeChange={setType} 
            onSortChange={setSortBy} 
            onViewModeChange={setViewMode} 
          />
        </div>
      </div>

      <div className="mt-4">
        <SlabResults 
          isLoading={isLoading} 
          error={error} 
          slabs={filteredSlabs} 
          viewMode={viewMode} 
          onSlabClick={setSelectedSlab} 
        />
      </div>

      {selectedSlab && (
        <SlabDetails 
          slab={selectedSlab} 
          open={!!selectedSlab} 
          onClose={() => setSelectedSlab(null)} 
          onUpdate={handleSlabUpdate} 
        />
      )}

      <footer className="mt-8 py-4 text-center text-xs text-muted-foreground/50">
        <p>Code by: Alisson Costa | Powered by alisson.ai</p>
      </footer>
    </div>
  );
}

export default Index;
